function set(r: number, g: number, b: number, out: number[]) {
  out[0] = Math.round(r * 255)
  out[1] = Math.round(g * 255)
  out[2] = Math.round(b * 255)
}

function clamp(v: number, l: number, u: number) {
  return Math.max(l, Math.min(v, u))
}

// https://github.com/jankuca/hsb2rgb/blob/master/src/hsb2rgb.js
export function hsb2RGB(_hue: string, _saturation: string, _value: string) {
  const hue = (parseInt(_hue, 10) || 0) % 360

  let saturation = /%/.test(_saturation) ? parseInt(_saturation, 10) / 100 : parseFloat(_saturation)

  let value = /%/.test(_value) ? parseInt(_value, 10) / 100 : parseFloat(_value)

  saturation = Math.max(0, Math.min(saturation, 1))
  value = Math.max(0, Math.min(value, 1))

  let rgb
  if (saturation === 0) {
    return [Math.round(255 * value), Math.round(255 * value), Math.round(255 * value)]
  }

  const side = hue / 60
  const chroma = value * saturation
  const x = chroma * (1 - Math.abs((side % 2) - 1))
  const match = value - chroma

  switch (Math.floor(side)) {
    case 0:
      rgb = [chroma, x, 0]
      break
    case 1:
      rgb = [x, chroma, 0]
      break
    case 2:
      rgb = [0, chroma, x]
      break
    case 3:
      rgb = [0, x, chroma]
      break
    case 4:
      rgb = [x, 0, chroma]
      break
    case 5:
      rgb = [chroma, 0, x]
      break
    default:
      rgb = [0, 0, 0]
  }

  rgb[0] = Math.round(255 * (rgb[0] + match))
  rgb[1] = Math.round(255 * (rgb[1] + match))
  rgb[2] = Math.round(255 * (rgb[2] + match))

  return rgb
}

// https://github.com/tmpvar/hsv2rgb/blob/master/hsv2rgb.js
export function hsv2RGB(h: number, s: number, v: number) {
  const out = [0, 0, 0]

  h = h % 360
  s = clamp(s, 0, 1)
  v = clamp(v, 0, 1)

  // Grey
  if (!s) {
    out[0] = out[1] = out[2] = Math.ceil(v * 255)
  } else {
    const b = (1 - s) * v
    const vb = v - b
    const hm = h % 60
    switch ((h / 60) | 0) {
      case 0:
        set(v, (vb * h) / 60 + b, b, out)
        break
      case 1:
        set((vb * (60 - hm)) / 60 + b, v, b, out)
        break
      case 2:
        set(b, v, (vb * hm) / 60 + b, out)
        break
      case 3:
        set(b, (vb * (60 - hm)) / 60 + b, v, out)
        break
      case 4:
        set((vb * hm) / 60 + b, b, v, out)
        break
      case 5:
        set(v, b, (vb * (60 - hm)) / 60 + b, out)
        break
    }
  }
  return out
}

export function num2RGB(_num: number) {
  return `rgb(${hsv2RGB(_num, 0.21, 1).join(', ')})`
}

export function hex2RGB(hex: string): [number, number, number] | null {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
  hex = hex.replace(shorthandRegex, function (m, r, g, b) {
    return r + r + g + g + b + b
  })

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)

  return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : null
}

export function hex2RGBA(hex: string, alpha: number) {
  return buildRGBA(hex2RGB(hex) || [0, 0, 0], alpha)
}

export function buildRGBA(rgb: [number, number, number], alpha: number) {
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${(alpha / 100).toFixed(2)})`
}
